import React, { useState, useRef, useEffect } from 'react';

const LANGUAGES = [
  { code: '', label: 'English (Reset)', short: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'Arabic — العربية', short: 'العربية', flag: '🇸🇦' },
  { code: 'bn', label: 'Bengali — বাংলা', short: 'বাংলা', flag: '🇧🇩' },
  { code: 'zh-CN', label: 'Chinese — 中文', short: '中文', flag: '🇨🇳' },
  { code: 'cs', label: 'Czech — Čeština', short: 'Čeština', flag: '🇨🇿' },
  { code: 'da', label: 'Danish — Dansk', short: 'Dansk', flag: '🇩🇰' },
  { code: 'nl', label: 'Dutch — Nederlands', short: 'Nederlands', flag: '🇳🇱' },
  { code: 'tl', label: 'Filipino', short: 'Filipino', flag: '🇵🇭' },
  { code: 'fi', label: 'Finnish — Suomi', short: 'Suomi', flag: '🇫🇮' },
  { code: 'fr', label: 'French — Français', short: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'German — Deutsch', short: 'Deutsch', flag: '🇩🇪' },
  { code: 'el', label: 'Greek — Ελληνικά', short: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'iw', label: 'Hebrew — עברית', short: 'עברית', flag: '🇮🇱' },
  { code: 'hi', label: 'Hindi — हिन्दी', short: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hu', label: 'Hungarian — Magyar', short: 'Magyar', flag: '🇭🇺' },
  { code: 'id', label: 'Indonesian — Bahasa', short: 'Bahasa', flag: '🇮🇩' },
  { code: 'it', label: 'Italian — Italiano', short: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', label: 'Japanese — 日本語', short: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: 'Korean — 한국어', short: '한국어', flag: '🇰🇷' },
  { code: 'lt', label: 'Lithuanian — Lietuvių', short: 'Lietuvių', flag: '🇱🇹' },
  { code: 'ms', label: 'Malay — Melayu', short: 'Melayu', flag: '🇲🇾' },
  { code: 'no', label: 'Norwegian — Norsk', short: 'Norsk', flag: '🇳🇴' },
  { code: 'pl', label: 'Polish — Polski', short: 'Polski', flag: '🇵🇱' },
  { code: 'pt', label: 'Portuguese — Português', short: 'Português', flag: '🇵🇹' },
  { code: 'ro', label: 'Romanian — Română', short: 'Română', flag: '🇷🇴' },
  { code: 'ru', label: 'Russian — Русский', short: 'Русский', flag: '🇷🇺' },
  { code: 'es', label: 'Spanish — Español', short: 'Español', flag: '🇪🇸' },
  { code: 'sv', label: 'Swedish — Svenska', short: 'Svenska', flag: '🇸🇪' },
  { code: 'ta', label: 'Tamil — தமிழ்', short: 'தமிழ்', flag: '🇮🇳' },
  { code: 'th', label: 'Thai — ไทย', short: 'ไทย', flag: '🇹🇭' },
  { code: 'tr', label: 'Turkish — Türkçe', short: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', label: 'Ukrainian — Українська', short: 'Українська', flag: '🇺🇦' },
  { code: 'vi', label: 'Vietnamese — Tiếng Việt', short: 'Tiếng Việt', flag: '🇻🇳' },
];

const LanguageSelector = ({ compact }) => {
  const [open, setOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  // Detect current language from Google Translate cookie
  useEffect(() => {
    const match = document.cookie.match(/googtrans=\/en\/([a-z-]+)/i);
    if (match) setCurrentLang(match[1]);
  }, []);

  // Click outside or page scroll to close (ignore scroll inside dropdown)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    const scrollHandler = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('scroll', scrollHandler, true);
    return () => { document.removeEventListener('mousedown', handler); window.removeEventListener('scroll', scrollHandler, true); };
  }, [open]);

  const handleSelect = (langCode) => {
    setOpen(false);
    if (!langCode && currentLang) {
      // Reset to English — clear googtrans cookie and reload
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + window.location.hostname;
      window.location.reload();
      return;
    }
    if (langCode && langCode !== currentLang) {
      // Set the Google Translate cookie directly and reload — most reliable method
      document.cookie = 'googtrans=/en/' + langCode + '; path=/;';
      document.cookie = 'googtrans=/en/' + langCode + '; path=/; domain=.' + window.location.hostname;
      window.location.reload();
    }
  };

  const currentEntry = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];
  const shortLabel = currentEntry.short;
  const currentFlag = currentEntry.flag;

  // ── COMPACT: mobile hamburger menu item ──
  if (compact) {
    return (
      <div ref={ref} style={{ position: 'relative', width: '100%' }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '10px',
            color: '#e2e8f0', padding: '14px 16px', fontSize: '16px', fontWeight: '600',
            cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        >
          <span>{currentFlag} {shortLabel}</span>
          <span style={{ fontSize: '11px', opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0,
            background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(124,58,237,0.4)',
            borderRadius: '10px', boxShadow: '0 -10px 30px rgba(0,0,0,0.5)',
            maxHeight: '250px', overflowY: 'auto', zIndex: 1000
          }}>
            {LANGUAGES.map(l => (
              <button key={l.code} type="button" onClick={() => handleSelect(l.code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '12px 16px',
                  background: currentLang === l.code ? 'rgba(124,58,237,0.18)' : 'transparent',
                  border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  color: '#e2e8f0', cursor: 'pointer', textAlign: 'left',
                  fontSize: '14px', fontWeight: currentLang === l.code ? '700' : '500',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = currentLang === l.code ? 'rgba(124,58,237,0.18)' : 'transparent'; }}
              >
                <span style={{ fontSize: '18px' }}>{l.flag}</span>
                <span style={{ flex: 1 }}>{l.label}</span>
                {currentLang === l.code && <span>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP: nav-bar pill matching CurrencySelector ──
  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setOpen(!open);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)',
          border: '1.5px solid rgba(99,102,241,0.3)',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(99,102,241,0.2)',
          color: '#ffffff', fontSize: '12px', fontWeight: '600',
          cursor: 'pointer', transition: 'all 0.2s ease',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.6)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <span style={{ fontSize: '14px' }}>{currentFlag}</span>
        <span>{shortLabel}</span>
        <span style={{ fontSize: '9px', opacity: 0.5, marginLeft: '2px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'fixed', top: dropPos.top + 'px', right: dropPos.right + 'px',
          width: '230px',
          background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(124,58,237,0.4)',
          borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          maxHeight: '60vh', overflowY: 'auto', zIndex: 10000,
          backdropFilter: 'blur(8px)'
        }}>
          {LANGUAGES.map(l => (
            <button key={l.code} type="button" onClick={() => handleSelect(l.code)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                width: '100%', padding: '10px 14px',
                background: currentLang === l.code ? 'rgba(124,58,237,0.18)' : 'transparent',
                border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                color: '#e2e8f0', cursor: 'pointer', textAlign: 'left',
                fontSize: '12px', fontWeight: currentLang === l.code ? '700' : '500',
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = currentLang === l.code ? 'rgba(124,58,237,0.18)' : 'transparent'; }}
            >
              <span style={{ fontSize: '15px' }}>{l.flag}</span>
              <span style={{ flex: 1 }}>{l.label}</span>
              {currentLang === l.code && <span style={{ fontSize: '13px', color: '#8b5cf6' }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
